// The Balance card's Tokens and USDT lines, from the holdings' total (the
// server's rule, positions_read.go portfolioFromHoldings: the total is the sum
// of the rows that state a value).

import type { WireHolding } from '../api/portfolio'

function finiteOrNull(v: string | null | undefined): number | null {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/** The ONE GRAM/USD rate the Balance card states every figure at: the
 *  positions read's ton_usd_rate (our TON/USD, the rate its GRAM totals are
 *  stated at). The GRAM line's dollars, the total in both units, the Tokens
 *  and USDT lines all use it (the operator, 2026-11-04: the GRAM line's
 *  dollars were at a client rate ~1% off the totals'). null: not read — the
 *  figures that need it are "—", never another rate's. */
export function balanceRate(
  portfolio: { ton_usd_rate?: string } | null | undefined
): number | null {
  const r = finiteOrNull(portfolio?.ton_usd_rate)
  return r !== null && r > 0 ? r : null
}

/** What the positions other than USDT are worth: the holdings less USDT's.
 *  The server's total is the sum of the rows that state a value (operator
 *  rule 2026-11-03), so a USDT row with no value (its balance unread) is not
 *  in it: nothing to take off — never "—" over a total that is known (live
 *  check of #1230, 2026-11-04). */
export function tokensUsdOf(
  holdingsUsd: string | null,
  row: WireHolding | undefined,
  usdtInTotal?: number
): number | null {
  const all = holdingsUsd == null || holdingsUsd === '' ? Number.NaN : Number(holdingsUsd)
  if (!Number.isFinite(all)) return null
  // several wallets merged: the USDT each one counted in its own total (the
  // merged USDT row's value is "" when one wallet's is unknown)
  if (typeof usdtInTotal === 'number' && Number.isFinite(usdtInTotal)) return all - usdtInTotal
  return all - usdtValueUsd(row)
}

/** USDT's dollars inside the holdings' total: its stated value, else 0 (a row
 *  without a value is not in the total). */
export function usdtValueUsd(row: WireHolding | undefined): number {
  if (!row || row.current_value_usd == null || row.current_value_usd === '') return 0
  const v = Number(row.current_value_usd)
  return Number.isFinite(v) ? v : 0
}

/** The tokens' GRAM value as served: the positions' less USDT's; null when a
 *  part is not served. With the read's rate (ton_usd_rate: the positions read
 *  states its GRAM totals as its USD ones at it) the tokens' and USDT's GRAM
 *  lines are their dollars at that rate — a USDT row never carries GRAM. */
export function tokensTonOf(
  holdingsTon: string | null,
  row: WireHolding | undefined,
  rate: number | null,
  tokensUsd: number | null
): number | null {
  if (rate !== null && rate > 0) return tokensUsd === null ? null : tokensUsd / rate
  const all = finiteOrNull(holdingsTon)
  if (all == null) return null
  if (!row) return all
  const usdt = finiteOrNull(row.current_value_ton)
  return usdt == null ? null : all - usdt
}
